/**
 * The fleet table: one row per product, one dot per stage of the value chain.
 *
 * Deliberately DIFFERENT from the POC here: the POC held the whole index in memory and
 * could sort and count per stage column across the entire fleet. This table is
 * server-paginated, so a per-stage sort or a column tally would silently describe the
 * visible page only. Sorting is therefore limited to what the API sorts fleet-wide
 * (name, brand, id), and the fleet-wide numbers live in the systemic strip above.
 */
import React, { useState } from 'react';
import type { FleetProduct, FleetQuery } from '@/services/productCockpitService';
import { STAGES, DEFS, firstActionable, fmtInt } from '../lib/stages';
import { StageDot } from './StageDot';
import { ProvenanceBar } from './ProvenanceBar';

type SortKey = NonNullable<FleetQuery['sort']>;

interface FleetTableProps {
  products: FleetProduct[];
  sort: SortKey;
  onSort: (key: SortKey) => void;
  onOpen: (product: FleetProduct) => void;
  loading: boolean;
}

const SORTABLE: Array<{ key: SortKey; label: string }> = [
  { key: 'name', label: 'product' },
  { key: 'brand', label: 'brand' },
  { key: 'product_id', label: 'id' },
];

export function FleetTable({ products, sort, onSort, onOpen, loading }: FleetTableProps) {
  // a feed image that 404s shows the SAME placeholder as a product with no image at all,
  // rather than collapsing to an empty cell that reads as a rendering bug
  const [brokenImages, setBrokenImages] = useState<ReadonlySet<string>>(new Set());

  if (!loading && !products.length) {
    return <div className="pmc-empty">Nothing matches these filters.</div>;
  }

  return (
    <div className="pmc-tablewrap">
      <table className="pmc-data">
        <thead>
          <tr>
            <th aria-label="image" />
            {SORTABLE.map(({ key, label }) => (
              <th
                key={key}
                className={`pmc-sortable${key === 'product_id' ? ' pmc-mono' : ''}`}
                onClick={() => onSort(key)}
                data-tip={`Sort the whole fleet by ${label} (server-side).`}
              >
                {label}
                {sort === key ? ' ↑' : ''}
              </th>
            ))}
            {STAGES.map((s) => (
              <th key={s} className="pmc-c" data-tip={DEFS[s]}>
                {s.slice(0, 9)}
              </th>
            ))}
            <th className="pmc-c" data-tip={DEFS.provenance}>provenance</th>
            <th className="pmc-num" data-tip={DEFS.gsc}>search (GSC)</th>
          </tr>
        </thead>
        <tbody style={loading ? { opacity: 0.45 } : undefined}>
          {products.map((p) => {
            const ring = firstActionable(p.stages);
            return (
              <tr
                key={p.product_id}
                className="pmc-row"
                tabIndex={0}
                onClick={() => onOpen(p)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(p); }
                }}
              >
                <td>
                  {p.image_url && !brokenImages.has(p.product_id)
                    ? (
                      <img
                        className="pmc-thumb"
                        src={p.image_url}
                        alt=""
                        loading="lazy"
                        onError={() => setBrokenImages((cur) => new Set(cur).add(p.product_id))}
                      />
                    )
                    : (
                      <span
                        className="pmc-thumb-fallback"
                        data-tip={p.image_url ? 'the feed image URL did not load' : 'no image in the feed'}
                      >
                        —
                      </span>
                    )}
                </td>
                <td>
                  <div className="pmc-name">{p.name || '(unnamed)'}</div>
                  {/* the leaf is a display of the KEY every stage below was read for, so the
                      full path and where it came from are one hover away — a row whose key
                      came from the resolver rather than a stored path is a weaker claim */}
                  <div
                    className="pmc-small pmc-mute"
                    title={`${p.category_path || '—'} (from ${p.category_path_source})`}
                  >
                    {p.category || '—'}
                    {p.cross_sector ? ' · cross-sector pool' : ''}
                    {/*
                      ⛔ A KEY THAT IS NOT A KEY IS VISIBLE ON THE ROW, NOT ONLY ON HOVER.
                      When the resolver cannot produce a key the cockpit uses the feed
                      category unchanged, and the INTERPRET and MATCH dots on this very row
                      were read with it. A title attribute is not where you put the reason a
                      reader should distrust the dots beside it.
                    */}
                    {p.category_key_unresolved || p.category_key_is_generations === false ? (
                      <span className="pmc-keywarn" title={`not the key generation used — ${p.category_path_source}`}>
                        {' · '}⚠ {p.category_key_unresolved ? 'unresolved key' : "not generation's key"}
                      </span>
                    ) : null}
                  </div>
                </td>
                <td className="pmc-small">{p.brand || <span className="pmc-faint">—</span>}</td>
                <td className="pmc-mono pmc-small">{p.product_id}</td>
                {STAGES.map((s) => (
                  <td key={s} className="pmc-c">
                    <StageDot stage={s} state={p.stages?.[s] ?? 'unknown'} ringed={ring === s} />
                  </td>
                ))}
                <td className="pmc-c">
                  {p.prov
                    ? <ProvenanceBar traced={p.prov.traced} total={p.prov.total} />
                    : <span className="pmc-faint" data-tip="no knowledge-base entry for this product">—</span>}
                </td>
                <td className="pmc-num">
                  {p.gsc && p.gsc.impressions
                    ? (
                      <>
                        <b>{fmtInt(p.gsc.impressions)}</b>
                        <span className="pmc-small pmc-mute">
                          {` impr · pos ${p.gsc.avg_position ?? '—'}`}
                        </span>
                      </>
                    )
                    : (
                      <span
                        className="pmc-small pmc-faint"
                        data-tip="no gsc_performance_data rows matched this page's public id — often a pre-publication or collector-window gap, not proof of silence"
                      >
                        none recorded
                      </span>
                    )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
